import { EMPTY_PACKAGING_KINDS } from './adr.js';
/**
 * Validation schemas of the core entities (Functional Specifications, sections 7 and 14).
 * The API validates requests with them and the web and mobile forms reuse them.
 */
import { z } from 'zod';
import { AUTHORIZATION_KINDS, BSD_TYPES, CONDITIONNEMENT_CATEGORIES, HAZARDOUS_WASTE_TYPES, consistencySchema, localeSchema, prestataireTypeSchema, wasteTypeSchema, } from './enums.js';
import { isNotSpecifiedElsewhere, isValidCmdCode, normalizeCmdCode } from './cmd.js';
import { SIGNATURE_MODES } from './signature.js';
export const uuidSchema = z.string().uuid();
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Date must be YYYY-MM-DD');
/** BR-25: ICE is 15 digits; RC and IF are checked for format only. */
export const iceSchema = z.string().regex(/^\d{15}$/, 'ICE must be 15 digits');
export const rcSchema = z.string().trim().min(1).max(30);
export const ifSchema = z.string().regex(/^\d{7,8}$/, 'IF must be 7 or 8 digits');
export const phoneSchema = z.string().regex(/^\+?[0-9 ]{9,20}$/);
/** An administrator of a tenant invited by the vendor. */
export const tenantAdminSchema = z.object({
    email: z.string().trim().toLowerCase().email().max(200),
    firstName: z.string().trim().min(1).max(80),
    lastName: z.string().trim().min(1).max(80),
});
export const tenantCreateSchema = z.object({
    name: z.string().trim().min(2).max(120),
    defaultLocale: localeSchema.default('fr'),
    /** Alias of the Keycloak organization (lowercase, digits and dashes); derived from the name when absent. */
    alias: z
        .string()
        .regex(/^[a-z0-9][a-z0-9-]{1,38}[a-z0-9]$/)
        .optional(),
    /** E-mail domains routed to this organization at login. */
    domains: z
        .array(z
        .string()
        .trim()
        .toLowerCase()
        .regex(/^[a-z0-9.-]+\.[a-z]{2,}$/))
        .default([]),
    /**
     * First administrator of the tenant, invited through Keycloak with the tenant administrator
     * role, which they receive at their first sign-in.
     */
    admin: tenantAdminSchema.optional(),
});
/**
 * Vendor console (Technical Specifications 15.3): why the vendor suspends a client or lifts the
 * suspension, kept on the client and in its audit trail.
 */
export const tenantStatusChangeSchema = z.object({
    reason: z.string().trim().min(3).max(500),
});
/**
 * Where a client stands in setting the product up, in the order the work is done: an
 * administrator signed in, a site, its wastes, its prestataires, the CAP of its hazardous
 * wastes, a team beyond the administrators, a first shipment.
 */
export const CLIENT_ONBOARDING_STEPS = [
    'ADMIN',
    'SITE',
    'WASTES',
    'PRESTATAIRES',
    'CAPS',
    'TEAM',
    'SHIPMENT',
];
/** FR-PAR-35: the collection-transport authorization a generator holds for its own waste. */
export const ownTransportAuthorizationSchema = z
    .object({
    number: z.string().trim().min(1).max(80),
    issuingAuthority: z.string().trim().max(200).optional(),
    validFrom: isoDate,
    validTo: isoDate,
    notes: z.string().trim().max(1000).optional(),
})
    .refine((a) => a.validTo >= a.validFrom, {
    path: ['validTo'],
    message: 'validTo must be on or after validFrom',
});
export const companyCreateSchema = z.object({
    legalName: z.string().trim().min(2).max(200),
    ice: iceSchema.optional(),
    rc: rcSchema.optional(),
    if: ifSchema.optional(),
    foreignIdentifier: z.string().trim().max(60).optional(),
    address: z.string().trim().max(400).optional(),
    city: z.string().trim().max(120).optional(),
    country: z.string().length(2).default('MA'),
    /** FR-PAR-35; null removes it. */
    ownTransportAuthorization: ownTransportAuthorizationSchema.nullable().optional(),
});
export const responsableDechetsSchema = z.object({
    name: z.string().trim().min(2).max(120),
    function: z.string().trim().max(120).optional(),
    email: z.string().email(),
    phone: phoneSchema.optional(),
});
export const numberingMaskSchema = z
    .string()
    .min(6)
    .max(60)
    .refine((m) => m.includes('{YEAR}') && /\{AUTOID(:\d{1,2})?\}/.test(m), 'Mask must contain {YEAR} and {AUTOID}');
/**
 * What FR-PAR-47 copies from one site to a new site of the tenant. CAP never: a CAP is granted to
 * the producing site, which asks for its own; nor the site's identity, stamp and BSD numbering.
 */
export const SITE_COPY_PARTS = [
    'preferences',
    'origins',
    'wastes',
    'conditionnements',
    'prestataires',
    'routes',
];
/** A filière names its waste and prestataires activated on the site, so it brings them along. */
export const SITE_COPY_REQUIRES = {
    preferences: [],
    origins: [],
    wastes: [],
    conditionnements: [],
    prestataires: [],
    routes: ['wastes', 'prestataires'],
};
/** The parts with the ones they need, in the order of SITE_COPY_PARTS. */
export function withCopyDependencies(parts) {
    const all = new Set(parts);
    for (const part of parts)
        for (const needed of SITE_COPY_REQUIRES[part])
            all.add(needed);
    return SITE_COPY_PARTS.filter((p) => all.has(p));
}
export const siteCopySchema = z.object({
    sourceSiteId: uuidSchema,
    parts: z.array(z.enum(SITE_COPY_PARTS)).min(1).transform(withCopyDependencies),
});
export const siteCreateSchema = z.object({
    companyId: uuidSchema,
    name: z.string().trim().min(2).max(160),
    address: z.string().trim().max(400).optional(),
    city: z.string().trim().max(120).optional(),
    region: z.string().trim().max(120).optional(),
    phone: phoneSchema.optional(),
    timezone: z.string().default('Africa/Casablanca'),
    responsableDechets: responsableDechetsSchema,
    activityDescription: z.string().trim().max(1000).optional(),
    numberingMask: numberingMaskSchema.default('{YEAR}{MONTH}-{AUTOID}'),
    numberingResetYearly: z.boolean().default(true),
    documentLocale: localeSchema.default('fr'),
    bsdCopies: z.number().int().min(1).max(10).default(5),
    /** FR-PAR-47: start from the configuration of another site of the tenant. */
    copyFrom: siteCopySchema.optional(),
});
export const originCreateSchema = z.object({
    siteId: uuidSchema,
    parentId: uuidSchema.nullable().default(null),
    name: z.string().trim().min(1).max(160),
    collectionAddress: z.string().trim().max(400).optional(),
    parcelNumber: z.string().trim().max(60).optional(),
    costCenter: z.string().trim().max(60).optional(),
});
export const conditionnementCreateSchema = z.object({
    siteId: uuidSchema,
    name: z.string().trim().min(1).max(120),
    category: z.enum(CONDITIONNEMENT_CATEGORIES),
    volumeL: z.number().positive().max(100000).optional(),
    packagingCode: z.string().trim().max(20).optional(),
    tareKg: z.number().nonnegative().max(100000).optional(),
    returnable: z.boolean().default(false),
});
/** The ADR block of a waste (FR-PAR-17). Manual fields describe a UN entry absent from the table. */
export const wasteAdrBlockSchema = z.object({
    identified: z.boolean().default(false),
    unNumber: z
        .string()
        .regex(/^\d{4}$/)
        .optional(),
    packingGroup: z.enum(['I', 'II', 'III']).optional(),
    technicalName: z.string().trim().max(200).optional(),
    /** The alternative of the proper shipping name, when table A lists several (ADR 3.1.2.1). */
    shippingNameChoice: z.string().trim().max(300).optional(),
    /**
     * The line of table A, by its description, when the UN number has several for the packing
     * group that differ (aerosols flammable or not, paints viscous or not).
     */
    variant: z.string().trim().max(1000).optional(),
    environmentallyHazardous: z.boolean().default(false),
    conformsTo2_1_3_5_5: z.boolean().default(false),
    emptyPackaging: z.boolean().default(false),
    /** ADR 5.4.1.1.6: the kind of empty means of containment and the form of its description. */
    emptyPackagingKind: z.enum(EMPTY_PACKAGING_KINDS).optional(),
    emptyPackagingDescription: z.enum(['SHORT', 'FULL']).optional(),
    /** ADR 1.1.3.5: the empty uncleaned packagings are not subject to ADR. */
    exempt1135: z.boolean().optional(),
    nsaComment: z.string().trim().max(500).optional(),
    tmdComment: z.string().trim().max(500).optional(),
    mentionOverride: z.string().trim().max(500).optional(),
    mentionOverrideReason: z.string().trim().max(500).optional(),
    manual: z
        .object({
        properShippingName: z.string().trim().min(3).max(300),
        classCode: z.string().trim().min(1).max(10),
        classificationCode: z.string().trim().max(10).optional(),
        packingGroup: z.enum(['I', 'II', 'III']).optional(),
        labels: z.array(z.string().trim().min(1)).default([]),
        tunnelCode: z.string().trim().max(10).optional(),
        transportCategory: z.number().int().min(0).max(4),
        requiresTechnicalName: z.boolean().default(false),
    })
        .optional(),
});
const wasteBaseSchema = z.object({
    siteId: uuidSchema,
    name: z.string().trim().min(2).max(250),
    cmdCode: z.string().transform((v, ctx) => {
        try {
            return normalizeCmdCode(v);
        }
        catch {
            ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Invalid CMD code' });
            return z.NEVER;
        }
    }),
    cmdIsHazardous: z.boolean(),
    type: wasteTypeSchema,
    typeJustification: z.string().trim().max(500).optional(),
    consistency: consistencySchema,
    pop: z.boolean().default(false),
    traceabilityAlert: z.boolean().default(false),
    internalNumber: z.string().trim().max(60).optional(),
    baselCode: z.string().trim().max(20).optional(),
    url: z.string().url().optional(),
    comment: z.string().trim().max(2000).optional(),
    adr: wasteAdrBlockSchema.default({}),
    customValues: z.record(z.unknown()).default({}),
    /** The stock above which an alert is raised (FR-TRV-02); null clears it. */
    maxStockT: z.number().positive().max(1_000_000).nullable().optional(),
    /** FR-COL-10: the storage duration above which the stock ageing alert fires; null: the site's. */
    maxStorageDays: z.number().int().positive().max(730).nullable().optional(),
});
/** What a client sends: the DD flag comes from the catalogue, not from the client. */
export const wasteInputSchema = wasteBaseSchema.omit({ cmdIsHazardous: true });
export const wasteUpdateSchema = wasteInputSchema.omit({ siteId: true }).partial();
/** Business rules on the complete waste (BR-01, BR-02, BR-03, BR-11). */
export const wasteCreateSchema = wasteBaseSchema.superRefine((w, ctx) => {
    if (!isValidCmdCode(w.cmdCode) || w.cmdCode.split(' ').length !== 3) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['cmdCode'],
            message: 'A waste needs a six-digit CMD entry',
        });
    }
    const hazardousType = HAZARDOUS_WASTE_TYPES.includes(w.type);
    if (w.cmdIsHazardous !== hazardousType && !w.typeJustification?.trim()) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['type'],
            message: 'BR-01: the type does not match the DD flag of the CMD entry; a justification is required',
        });
    }
    if (isNotSpecifiedElsewhere(w.cmdCode) && w.name.trim().length < 10) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['name'],
            message: 'BR-02: a 99 code needs a descriptive name',
        });
    }
    if (w.adr.mentionOverride && !w.adr.mentionOverrideReason?.trim()) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['adr', 'mentionOverrideReason'],
            message: 'BR-03: an override needs a reason',
        });
    }
    if (w.adr.identified && !w.adr.unNumber) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['adr', 'unNumber'],
            message: 'BR-11: an ADR waste needs a UN number',
        });
    }
});
export const contactSchema = z.object({
    name: z.string().trim().min(1).max(120),
    function: z.string().trim().max(120).optional(),
    email: z.string().email().optional(),
    phone: phoneSchema.optional(),
});
export const prestataireCreateSchema = z.object({
    name: z.string().trim().min(2).max(200),
    ice: iceSchema.optional(),
    rc: rcSchema.optional(),
    if: ifSchema.optional(),
    address: z.string().trim().max(400).optional(),
    city: z.string().trim().max(120).optional(),
    country: z.string().length(2).default('MA'),
    types: z.array(prestataireTypeSchema).min(1),
    contacts: z.array(contactSchema).default([]),
    notes: z.string().trim().max(2000).optional(),
});
export const prestataireUpdateSchema = prestataireCreateSchema.partial();
export const INSTALLATION_KINDS = [
    'TRAITEMENT',
    'ENTREPOSAGE',
    'TRANSIT_REGROUPEMENT',
    'DECHARGE_CLASSE_3',
];
export const installationCreateSchema = z.object({
    prestataireId: uuidSchema,
    name: z.string().trim().min(2).max(200),
    address: z.string().trim().max(400).optional(),
    city: z.string().trim().max(120).optional(),
    region: z.string().trim().max(120).optional(),
    kind: z.enum(INSTALLATION_KINDS),
    drCodes: z.array(z.string().regex(/^[DR]\d{1,2}$/)).default([]),
});
export const authorizationCreateSchema = z
    .object({
    prestataireId: uuidSchema,
    installationId: uuidSchema.optional(),
    kind: z.enum(AUTHORIZATION_KINDS),
    number: z.string().trim().min(1).max(80),
    issuingAuthority: z.string().trim().max(200).optional(),
    validFrom: isoDate,
    validTo: isoDate,
    cmdCodes: z.array(z.string()).default([]),
    notes: z.string().trim().max(2000).optional(),
})
    .refine((a) => a.validTo >= a.validFrom, {
    path: ['validTo'],
    message: 'validTo must be on or after validFrom',
});
export const prestataireActivationSchema = z.object({
    contact: contactSchema.optional(),
    instructions: z.string().trim().max(2000).optional(),
    active: z.boolean().default(true),
});
export const capCreateSchema = z
    .object({
    siteId: uuidSchema,
    wasteId: uuidSchema,
    installationId: uuidSchema,
    number: z.string().trim().min(1).max(80),
    validFrom: isoDate,
    validTo: isoDate,
    description: z.string().trim().max(1000).optional(),
    originId: uuidSchema.optional(),
    yearlyQuantityT: z.number().nonnegative().optional(),
    deliveryFrequency: z.string().trim().max(120).optional(),
})
    .refine((c) => c.validTo >= c.validFrom, {
    path: ['validTo'],
    message: 'validTo must be on or after validFrom',
});
export const capVoletBSchema = z.object({
    destinataireIdentification: z.string().trim().min(2).max(400),
    organizationalUnit: z.string().trim().max(200).optional(),
    installationDescription: z.string().trim().max(400).optional(),
    installationAuthorizationNumber: z.string().trim().max(80).optional(),
    treatmentTypes: z.array(z.string().trim().min(1)).min(1),
    acceptedAt: isoDate,
    contact: contactSchema.optional(),
});
/** Sending volet A to the destinataire by e-mail (FR-PAR-37). */
export const capSendSchema = z.object({
    to: z.array(z.string().trim().toLowerCase().email()).min(1).max(5),
    cc: z.array(z.string().trim().toLowerCase().email()).max(5).default([]),
    message: z.string().trim().max(2000).optional(),
});
export const capRenewSchema = z
    .object({
    number: z.string().trim().min(1).max(80),
    validFrom: isoDate,
    validTo: isoDate,
})
    .refine((c) => c.validTo >= c.validFrom, {
    path: ['validTo'],
    message: 'validTo must be on or after validFrom',
});
export const defaultRouteSchema = z.object({
    conditionnementId: uuidSchema.optional(),
    originId: uuidSchema.optional(),
    transporterId: uuidSchema.optional(),
    transporterAuthorizationId: uuidSchema.optional(),
    traderId: uuidSchema.optional(),
    storageInstallationId: uuidSchema.optional(),
    treatmentInstallationId: uuidSchema.optional(),
    drCode: z
        .string()
        .regex(/^[DR]\d{1,2}$/)
        .optional(),
    treatmentLabel: z.string().trim().max(200).optional(),
    bsdType: z.enum(BSD_TYPES).default('BSD_DANGEREUX'),
});
/** FR-PAR-44: an alternative filière of a waste, named, selectable at shipment time. */
export const routeCreateSchema = defaultRouteSchema.extend({
    name: z.string().trim().min(1).max(120),
});
export const routeUpdateSchema = routeCreateSchema.partial();
// ---------------------------------------------------------------------------------------------
// Updates (partial) and role assignments
// ---------------------------------------------------------------------------------------------
export const companyUpdateSchema = companyCreateSchema.partial();
export const siteUpdateSchema = siteCreateSchema
    .omit({ companyId: true, copyFrom: true })
    .partial()
    .extend({
    labelPreferences: z
        .object({
        showSiteAddress: z.boolean().default(true),
        showOrigin: z.boolean().default(true),
        showPrestataire: z.boolean().default(true),
        showCapNumber: z.boolean().default(false),
        showInternalNumber: z.boolean().default(false),
        showFreeZone: z.boolean().default(false),
        borderColor: z.enum(['NONE', 'RED', 'ORANGE', 'BLACK']).default('NONE'),
    })
        .optional(),
    traceabilityDelays: z
        .object({
        returnDays: z.number().int().min(1).max(365).default(30),
        treatmentDays: z.number().int().min(1).max(730).default(90),
        maxStorageDays: z.number().int().min(1).max(730).optional(),
        /** BR-20: gap between producer and receiver quantities flagged beyond it (FR-SUI-04). */
        quantityTolerancePercent: z.number().min(0).max(100).default(10),
        /** FR-COL-04: days within which a pickup request should be picked up. */
        pickupTargetDays: z.number().int().min(1).max(90).default(7),
    })
        .optional(),
    /** BR-07: whether a non-hazardous shipment needs a CAP too (FR-PAR-41, FR-COL-29). */
    capRequiredForNonHazardous: z.boolean().optional(),
    /** FR-FIA-18, FR-FIA-19: scanned paper copies, or the signature in the application. */
    signatureMode: z.enum(SIGNATURE_MODES).optional(),
    /** FR-FIA-09: the holder named on each BSD copy, in copy order; null, the defaults. */
    copyHolders: z.array(z.string().trim().min(1).max(80)).min(1).max(10).nullable().optional(),
    /** FR-TRV-10: the date of the plan interne de gestion in force, renewed every five years. */
    planInterneDate: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/)
        .nullable()
        .optional(),
    active: z.boolean().optional(),
});
export const originUpdateSchema = z.object({
    name: z.string().trim().min(1).max(160).optional(),
    parentId: uuidSchema.optional(),
    collectionAddress: z.string().trim().max(400).nullable().optional(),
    parcelNumber: z.string().trim().max(60).nullable().optional(),
    costCenter: z.string().trim().max(60).nullable().optional(),
    active: z.boolean().optional(),
});
export const conditionnementUpdateSchema = conditionnementCreateSchema
    .omit({ siteId: true })
    .partial()
    .extend({ active: z.boolean().optional() });
export const installationUpdateSchema = installationCreateSchema
    .omit({ prestataireId: true })
    .partial()
    .extend({ active: z.boolean().optional() });
export const authorizationUpdateSchema = z
    .object({
    number: z.string().trim().min(1).max(80).optional(),
    issuingAuthority: z.string().trim().max(200).nullable().optional(),
    validFrom: isoDate.optional(),
    validTo: isoDate.optional(),
    cmdCodes: z.array(z.string()).optional(),
    notes: z.string().trim().max(2000).nullable().optional(),
})
    .refine((a) => !a.validFrom || !a.validTo || a.validTo >= a.validFrom, {
    path: ['validTo'],
    message: 'validTo must be on or after validFrom',
});
//# sourceMappingURL=schemas.js.map